import React, { useRef, useState, useEffect } from 'react';
import { Globe2, DollarSign, Users, Swords, Radiation, ChevronRight, X, Megaphone, KeyRound, Shield, Handshake, AlertTriangle, UserCheck, TrendingUp, TrendingDown, Award, Plane, Flag, Zap, Radar } from 'lucide-react';
import EnrichmentPurchasePopup from './EnrichmentPurchasePopup';
import { getRelationship, ONBOARDING_LAST_TURN } from '../config/onboarding';

// Resource bar component - shows player resources + nuke button + enrichment purchase
export function ResourceBar({ resources, countryName, color, onStatusReportClick, onLaunchNuke, hasNukeReady, game, onRefreshGame }) {
  const [purchasing, setPurchasing] = useState(false);
  const [showEnrichmentPopup, setShowEnrichmentPopup] = useState(null);
  const currentEnrichment = resources?.uranium_enrichment || 0;
  const playerMoney = resources?.money || 0;
  const playerPopulation = resources?.population || 100;
  
  // Calculate dynamic pricing based on population
  const baseCost = 1000;
  const populationMultiplier = Math.max(0.8, Math.min(3.0, 0.8 + playerPopulation / 500));
  const actualCost = Math.floor(baseCost * populationMultiplier);
  const canPurchase = playerMoney >= actualCost && currentEnrichment < 100;
  
  const handlePurchaseEnrichment = async () => {
    if (!canPurchase || !game || purchasing) return;
    
    setPurchasing(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API_URL}/api/games/${game.id}/purchase-enrichment?amount=25`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      // ⭐ CRITICAL FIX: Check if response has content before reading JSON
      if (!response.ok) {
        // Try to read error message, but handle case where body is empty/already read
        let errorMessage = 'Failed to purchase enrichment';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (jsonError) {
          console.warn('Could not parse error response:', jsonError);
        }
        alert(`❌ ${errorMessage}`);
        setPurchasing(false);
        return;
      }
      
      // Success response
      const data = await response.json();
      
      // Show enrichment popup instead of alert
      setShowEnrichmentPopup({
        countryCode: game.countries.find(c => c.is_player)?.code || 'USA',
        countryName: countryName,
        enrichmentGained: data.enrichment_gained,
        cost: data.cost,
        newEnrichment: data.new_enrichment
      });
      
      setPurchasing(false);
      
      // Refresh game state after popup completes (NO window.location.reload!)
      setTimeout(() => {
        if (onRefreshGame) {
          onRefreshGame();
        }
      }, 3500);
      
    } catch (error) {
      console.error('Purchase enrichment error:', error);
      alert(`❌ Failed: ${error.message}`);
      setPurchasing(false);
    }
  };
  
  const items = [
    { key: 'population', label: 'POPULATION', icon: UserCheck, color: '#8b5cf6', suffix: 'M' },
    { key: 'money', label: 'FUNDS', icon: DollarSign, color: '#22c55e', suffix: 'M' },
    { key: 'influence', label: 'INFLUENCE', icon: Users, color: '#3b82f6', suffix: '' },
    { key: 'military', label: 'MILITARY', icon: Swords, color: '#ef4444', suffix: '' },
    { key: 'nukes', label: 'NUKES', icon: Radiation, color: '#f59e0b', suffix: '' },
  ];

  return (
    <div className="space-y-3 pointer-events-auto">
      {/* Enrichment Purchase Popup */}
      {showEnrichmentPopup && (
        <EnrichmentPurchasePopup
          {...showEnrichmentPopup}
          onComplete={() => setShowEnrichmentPopup(null)}
        />
      )}
      
      <div 
        className="bunker-panel p-4 bg-slate-900/75 backdrop-blur-md cursor-pointer hover:bg-slate-900/85 transition-colors" 
        data-testid="resource-bar"
        onClick={(e) => {
          console.log('🖱️ ResourceBar clicked! Opening Status Report...');
          onStatusReportClick && onStatusReportClick();
        }}
        title="Click to view full Status Report"
      >
        <div className="flex items-center gap-2 mb-3">
          <Globe2 className="w-5 h-5" style={{ color }} />
          <span className="font-heading text-lg tracking-wider" style={{ color }}>
            {countryName}
          </span>
          <span className="text-xs text-blue-400 ml-auto opacity-75">
            📊 Click for details
          </span>
        </div>
        <div className="grid grid-cols-5 gap-4">
          {items.map(({ key, label, icon: Icon, color: iconColor, suffix }) => (
            <div key={key} className="text-center">
              <Icon className="w-5 h-5 mx-auto mb-1" style={{ color: iconColor }} />
              <div className="text-[10px] text-muted uppercase tracking-tight leading-tight whitespace-nowrap overflow-hidden text-ellipsis">{label}</div>
              <div className="font-mono text-xl font-bold mt-1" style={{ color: iconColor }}>
                {resources[key]}{suffix}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* LAUNCH NUKE BUTTON - WITH INTENSE GLOW EFFECT */}
      {hasNukeReady && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onLaunchNuke && onLaunchNuke();
          }}
          className="relative w-full bunker-panel p-4 bg-red-900/50 hover:bg-red-900/70 border-2 border-red-500 transition-all cursor-pointer group overflow-hidden"
          style={{
            boxShadow: '0 0 30px rgba(239, 68, 68, 0.8), 0 0 60px rgba(239, 68, 68, 0.4), inset 0 0 20px rgba(239, 68, 68, 0.2)',
            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
          }}
          title="Select a target country to launch nuclear strike"
        >
          {/* Rotating burning glow effect */}
          <div 
            className="absolute inset-0 opacity-40"
            style={{
              background: 'conic-gradient(from 0deg, transparent, rgba(239, 68, 68, 0.8), transparent)',
              animation: 'spin 4s linear infinite',
              filter: 'blur(12px)'
            }}
          ></div>
          
          <div className="relative flex items-center justify-center gap-3 z-10">
            <Radiation className="w-6 h-6 text-red-500 animate-pulse" />
            <div className="text-center">
              <div className="font-heading text-xl tracking-wider text-red-500 group-hover:text-red-400 uppercase animate-pulse">
                🚀 LAUNCH NUCLEAR STRIKE
              </div>
              <div className="text-xs text-red-400 mt-1">
                Click a target country on the map
              </div>
            </div>
            <Radiation className="w-6 h-6 text-red-500 animate-pulse" />
          </div>
        </button>
      )}
      
      {/* BUY ENRICHMENT BUTTON - Shows when not at 100% */}
      {currentEnrichment < 100 && (
        <button
          onClick={handlePurchaseEnrichment}
          disabled={!canPurchase || purchasing}
          className={`relative w-full bunker-panel p-3 border-2 transition-all cursor-pointer group overflow-hidden
            ${canPurchase 
              ? 'bg-purple-900/50 hover:bg-purple-900/70 border-purple-500' 
              : 'bg-gray-800/50 border-gray-600 opacity-50 cursor-not-allowed'
            }`}
          style={canPurchase ? {
            boxShadow: '0 0 20px rgba(168, 85, 247, 0.6), 0 0 40px rgba(168, 85, 247, 0.3)',
          } : {}}
          title={playerMoney < actualCost 
            ? `💰 Need $${actualCost}M, have $${playerMoney}M\n\n📊 Price: $${baseCost}M base × ${populationMultiplier.toFixed(2)} (pop: ${playerPopulation}M)` 
            : `☢️ Buy +25% enrichment for $${actualCost}M\n\n📊 Base: $${baseCost}M × ${populationMultiplier.toFixed(2)} population multiplier\n${currentEnrichment.toFixed(0)}% → ${Math.min(100, currentEnrichment + 25).toFixed(0)}%`}
        >
          <div className="relative flex items-center justify-center gap-2 z-10">
            <Zap className={`w-5 h-5 ${canPurchase ? 'text-purple-400' : 'text-gray-500'}`} />
            <div className="text-center">
              <div className={`font-bold text-sm tracking-wider uppercase ${canPurchase ? 'text-purple-400' : 'text-gray-500'}`}>
                {purchasing ? 'PURCHASING...' : '⚡ BUY +25% ENRICHMENT'}
              </div>
              <div className={`text-xs mt-0.5 ${canPurchase ? 'text-purple-300' : 'text-gray-600'}`}>
                ${actualCost}M (×{populationMultiplier.toFixed(1)} pop)
              </div>
            </div>
            <Zap className={`w-5 h-5 ${canPurchase ? 'text-purple-400' : 'text-gray-500'}`} />
          </div>
        </button>
      )}
    </div>
  );
}

// Turn indicator with surrender button and enrichment purchase
export function TurnIndicator({ turn, onEndTurn, isProcessing, onSurrender, shouldShowSurrender, game }) {
  const player = game?.countries?.find(c => c.is_player);
  const currentEnrichment = player?.resources?.uranium_enrichment || 0;
  const playerMoney = player?.resources?.money || 0;
  const canPurchase = playerMoney >= 1000 && currentEnrichment < 100;
  
  const handlePurchaseEnrichment = async () => {
    if (!canPurchase || !game) return;
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${API_URL}/api/games/${game.id}/purchase-enrichment?amount=25`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      // ⭐ CRITICAL FIX: Check if response has content before reading JSON
      if (!response.ok) {
        // Try to read error message, but handle case where body is empty/already read
        let errorMessage = 'Failed to purchase enrichment';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (jsonError) {
          console.warn('Could not parse error response:', jsonError);
        }
        alert(`❌ ${errorMessage}`);
        return;
      }
      
      // Success response
      const data = await response.json();
      alert(`✅ Purchased +25% enrichment for $${data.cost}M!\n\n${data.message}\n\nNew enrichment: ${data.new_enrichment.toFixed(0)}%`);
      window.location.reload();
      
    } catch (error) {
      console.error('Purchase enrichment error:', error);
      alert(`❌ Failed: ${error.message}`);
    }
  };
  
  return (
    <div 
      className="bunker-panel p-4 flex items-center justify-between bg-slate-900/75 backdrop-blur-md" 
      style={{ pointerEvents: 'auto' }}
      data-testid="turn-indicator"
    >
      <div>
        <div className="text-xs text-muted uppercase tracking-widest">GLOBAL TURN</div>
        <div className="font-heading text-3xl text-amber">{turn}</div>
        <div className="text-xs text-muted mt-1">Round {turn}</div>
        
        {/* Surrender button - shows when in losing position */}
        {shouldShowSurrender && (
          <button
            onClick={onSurrender}
            className="mt-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white text-xs font-bold py-2 px-4 rounded border-2 border-red-400 transition-all flex items-center gap-2"
            title="Surrender and end the game"
          >
            <Flag className="w-4 h-4" />
            SURRENDER
          </button>
        )}
      </div>
      
      {/* Enrichment Purchase Button */}
      <button
        onClick={handlePurchaseEnrichment}
        disabled={!canPurchase}
        className={`px-4 py-2 text-xs font-bold rounded-lg border-2 transition-all flex items-center gap-2
          ${canPurchase 
            ? 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white border-purple-400 transform hover:scale-105' 
            : 'bg-gray-700 text-gray-500 border-gray-600 cursor-not-allowed opacity-50'
          }`}
        style={{ pointerEvents: 'auto', cursor: canPurchase ? 'pointer' : 'not-allowed' }}
        title={currentEnrichment >= 100 
          ? '✅ Enrichment already at 100%' 
          : playerMoney < 1000 
          ? `💰 Need $1,000M, have $${playerMoney}M` 
          : `☢️ Buy +25% enrichment for $1,000M\n\n${currentEnrichment.toFixed(0)}% → ${Math.min(100, currentEnrichment + 25).toFixed(0)}%`}
      >
        <Radiation className="w-4 h-4" />
        BUY +25% ☢️
      </button>
      
      <button
        onClick={(e) => {
          console.log('🎯 NEXT ROUND button clicked!', e);
          onEndTurn();
        }}
        disabled={isProcessing}
        className="launch-button px-4 py-2 text-xs opacity-70 hover:opacity-100 transition-opacity"
        style={{ pointerEvents: 'auto', cursor: 'pointer' }}
        data-testid="end-turn-btn"
        title="Advance to next round (gives everyone 3 new actions)"
      >
        {isProcessing ? (
          <span className="animate-pulse">PROCESSING...</span>
        ) : (
          <>
            NEXT ROUND
            <ChevronRight className="w-4 h-4 inline ml-1" />
          </>
        )}
      </button>
    </div>
  );
}

// Country info panel
export function CountryPanel({ country, playerCountry, onAction, onClose, onStatusReportClick, gameId, game, isProcessingAiTurns }) {
  const [propagandaChance, setPropagandaChance] = React.useState(50);
  const [diplomacyChance, setDiplomacyChance] = React.useState(50);
  const [blackmailChance, setBlackmailChance] = React.useState(60);

  // 🎓 ONBOARDING — Highlight a suggested action button during turns 1-2.
  // Stays visible for ALL 3 actions of those turns (not just the first
  // click) so impatient players have time to read it; auto-dismisses
  // once turn 3 begins or actions run out.
  const currentTurn = game?.current_turn || 1;
  const playerActionsRemaining = playerCountry?.actions_remaining ?? 0;
  const showOnboardingHint =
    currentTurn <= ONBOARDING_LAST_TURN &&
    playerActionsRemaining > 0 &&
    playerCountry && country && playerCountry.code !== country.code;
  const relationship = showOnboardingHint
    ? getRelationship(playerCountry.code, country.code)
    : null;
  let hintActionType = null;
  let hintLabel = '';
  if (showOnboardingHint) {
    if (relationship === 'strong_ally' || relationship === 'semi_ally') {
      hintActionType = 'diplomacy';
      hintLabel = '🤝 Build alliance';
    } else if (relationship === 'enemy') {
      hintActionType = currentTurn >= 2 ? 'air_strike' : 'propaganda';
      hintLabel = currentTurn >= 2 ? '✈️ Now available!' : '☢️ Best opener';
    }
  }
  
  React.useEffect(() => {
    // Fetch current propaganda, diplomacy, and blackmail chances for this country pair
    if (playerCountry && country && gameId) {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Fetch propaganda chance
      fetch(`${BACKEND_URL}/api/games/${gameId}/propaganda-chance/${playerCountry.code}/${country.code}`)
        .then(res => res.json())
        .then(data => setPropagandaChance(data.current_chance))
        .catch(err => console.error('Failed to fetch propaganda chance:', err));
      
      // Fetch diplomacy chance
      fetch(`${BACKEND_URL}/api/games/${gameId}/diplomacy-chance/${playerCountry.code}/${country.code}`)
        .then(res => res.json())
        .then(data => setDiplomacyChance(data.current_chance))
        .catch(err => console.error('Failed to fetch diplomacy chance:', err));
      
      // Fetch blackmail chance
      fetch(`${BACKEND_URL}/api/games/${gameId}/blackmail-chance/${playerCountry.code}/${country.code}`)
        .then(res => res.json())
        .then(data => setBlackmailChance(data.current_chance))
        .catch(err => console.error('Failed to fetch blackmail chance:', err));
    }
  }, [playerCountry, country, gameId, game?.current_turn, playerCountry?.actions_remaining]); // ⭐ Refresh after each action (actions_remaining changes)
  
  if (!country) return null;
  
  // Check if this country is defeated/eliminated
  const isDefeated = country.is_eliminated || 
                     country.resources?.eliminated || 
                     (country.resources?.population || 0) <= 0;
  
  if (isDefeated) {
    // Don't render panel for defeated countries
    return null;
  }

  const isOwnCountry = country.code === playerCountry?.code;
  const relation = playerCountry?.relations?.[country.code] || 0;
  const canTakeAction = playerCountry?.actions_remaining > 0 && !isProcessingAiTurns; // Block actions during AI turns!
  
  const getRelationLabel = (val) => {
    if (val >= 50) return { text: 'ALLIED', color: '#22c55e' };
    if (val >= 0) return { text: 'NEUTRAL', color: '#f59e0b' };
    if (val >= -50) return { text: 'HOSTILE', color: '#ef4444' };
    return { text: 'AT WAR', color: '#ff3b30' };
  };

  const relationInfo = getRelationLabel(relation);

  // Helper function to generate tooltip for missing resources
  const getMissingResourcesTooltip = (actionType) => {
    // Handle nuclear strike
    if (actionType === 'nuclear_strike') {
      const required = { money: 50, influence: 30 };
      const current = playerCountry.resources;
      const missing = [];
      
      if (current.money < required.money) {
        missing.push(`💵 Money: $${Math.round(current.money)}M / $${required.money}M (need $${required.money - Math.round(current.money)}M more)`);
      }
      if (current.influence < required.influence) {
        missing.push(`👥 Influence: ${Math.round(current.influence)} / ${required.influence} (need ${required.influence - Math.round(current.influence)} more)`);
      }
      
      // Check if player has nukes
      if (playerCountry.resources.nukes <= 0) {
        return `⚠️ NO NUCLEAR WARHEADS\n\nYou need uranium enrichment to build nuclear weapons.\n\nCurrent Enrichment: ${Math.round(playerCountry.resources.enrichment || 0)}%\n\n🔬 Gain enrichment through:\n• Successful blackmail (+5%)\n• Deploy troops (drains -20% from target)\n\nOnce you reach 100% enrichment, you'll automatically build a nuke!`;
      }
      
      if (missing.length > 0) {
        return `⚠️ INSUFFICIENT RESOURCES\n\nRequired:\n💵 $${required.money}M Money\n👥 ${required.influence} Influence\n\nYou have:\n${missing.join('\n')}\n\nGain resources through successful propaganda, diplomacy, or blackmail!`;
      }
      
      return `☢️ NUCLEAR STRIKE\n\nDevastates target:\n• -50% Population (immediate)\n• 5 rounds of radiation fallout\n• Each fallout round: -10% more population\n\nCost:\n💵 $${required.money}M Money\n👥 ${required.influence} Influence\n🚀 1 Nuclear Warhead\n\n⚠️ WARNING: This will drastically worsen relations!`;
    }
    
    // Handle air strike
    if (actionType === 'air_strike') {
      const required = { money: 10, military: 5, influence: 5 };
      const current = playerCountry.resources;
      const missing = [];
      
      if (current.money < required.money) {
        missing.push(`💵 Money: $${Math.round(current.money)}M / $${required.money}M (need $${required.money - Math.round(current.money)}M more)`);
      }
      if (current.military < required.military) {
        missing.push(`⚔️ Military: ${Math.round(current.military)} / ${required.military} (need ${required.military - Math.round(current.military)} more)`);
      }
      if (current.influence < required.influence) {
        missing.push(`👥 Influence: ${Math.round(current.influence)} / ${required.influence} (need ${required.influence - Math.round(current.influence)} more)`);
      }
      
      if (missing.length > 0) {
        return `⚠️ INSUFFICIENT RESOURCES\n\nRequired:\n💵 $${required.money}M Money\n⚔️ ${required.military} Military\n👥 ${required.influence} Influence\n\nYou have:\n${missing.join('\n')}\n\nGain resources through successful propaganda, diplomacy, or blackmail!`;
      }
      return `✈️ AIR STRIKE (75% Success Rate)\n\nSUCCESS:\n• Target: -10% pop, -15% $, -15% inf, -5% enrich\n• You pay: 15% $, 10% mil, 10% inf\n\nFAILURE (Bomber Shot Down):\n• You lose: 15% $, 5% inf, 10% mil\n• 3-turn cooldown before next strike\n\nRisk vs Reward!`;
    }
    
    // Handle deploy troops
    if (actionType === 'deploy_troops') {
      const required = { money: 60, military: 20, influence: 15 };  // UPDATED: military reduced to 20
      const current = playerCountry.resources;
      const missing = [];
      
      if (current.money < required.money) {
        missing.push(`💵 Money: $${Math.round(current.money)}M / $${required.money}M (need $${required.money - Math.round(current.money)}M more)`);
      }
      if (current.military < required.military) {
        missing.push(`⚔️ Military: ${Math.round(current.military)} / ${required.military} (need ${required.military - Math.round(current.military)} more)`);
      }
      if (current.influence < required.influence) {
        missing.push(`👥 Influence: ${Math.round(current.influence)} / ${required.influence} (need ${required.influence - Math.round(current.influence)} more)`);
      }
      
      if (isDeployTroopsLocked) {
        return `🔒 DEPLOY TROOPS - ON COOLDOWN\n\nYour military needs to recover!\n\nWhen ready, troops:\n• Drain target: -5% resources/turn for 3 turns\n• Total damage: -15% money/military/influence/pop\n• Bonus: -20% enrichment from target\n\n⏰ 3-turn cooldown after each use`;
      }
      
      if (missing.length > 0) {
        return `⚠️ INSUFFICIENT RESOURCES\n\nRequired:\n💵 $${required.money}M Money\n⚔️ ${required.military} Military\n👥 ${required.influence} Influence\n\nYou have:\n${missing.join('\n')}\n\nGain resources through successful propaganda, diplomacy, or blackmail!`;
      }
      return `🪖 DEPLOY TROOPS (3 Turns)\n\nDrains target over 3 rounds:\n• Each turn: -5% money/military/influence/pop\n• Total: -15% resources, -20% enrichment\n\nCost: $${required.money}M, ${required.military} Mil, ${required.influence} Inf\n\n⏰ 3-turn cooldown after use`;
    }
    
    // Handle propaganda (no resource requirements, just chance-based)
    if (actionType === 'propaganda') {
      return `📢 PROPAGANDA (${propagandaChance}% Success Rate)\n\nSUCCESS:\n• Steal 10% pop + money + 15 influence from target\n\nFAILURE:\n• Target gains 5% of your resources instead\n\n💡 Success rate improves with better relations!`;
    }
    
    // Handle blackmail (no resource requirements, just chance-based)
    if (actionType === 'blackmail') {
      return `🕵️ BLACKMAIL (${blackmailChance}% Success Rate)\n\nSUCCESS:\n• Steal 12% money + 5 influence + 5% enrichment\n\nFAILURE:\n• You lose: -8 influence, -5% money\n• Future blackmail chances reduced by 5%\n\n⚠️ High risk, high reward!`;
    }
    
    // Handle diplomacy (no resource requirements, just chance-based)
    if (actionType === 'diplomacy') {
      return `🤝 DIPLOMACY (${diplomacyChance}% Success Rate)\n\nSUCCESS:\n• Improve relations significantly\n• Immigration bonus\n• +8% propaganda success next time\n• +5 influence\n• +5 military (military aid from ally)\n\nFAILURE:\n• Still get minor relation improvement\n\n💡 Always worth trying!`;
    }
    
    // Handle train_military (new action)
    if (actionType === 'train_military') {
      const required = { money: 20 };
      const current = playerCountry.resources;
      
      if (current.money < required.money) {
        return `⚠️ INSUFFICIENT FUNDS\n\nRequired:\n💵 $${required.money}M Money\n\nYou have:\n💵 Money: $${Math.round(current.money)}M / $${required.money}M (need $${required.money - Math.round(current.money)}M more)\n\nGain money through successful propaganda, blackmail, or wait for passive income!`;
      }
      
      return `🪖 TRAIN MILITARY\n\nRecruit and train new military forces!\n\nCost: 💵 $${required.money}M Money\nGain: ⚔️ +10 Military\n\n💡 Great way to rebuild your military after expensive operations!\n\nNote: You also gain +2 military per turn passively (training & recruitment).`;
    }
    
    return '';
  };

  // Check if deploy troops is LOCKED (requires nuke to unlock)
  const isDeployTroopsLocked = game.deploy_troops_locked?.[playerCountry.code] || false;
  
  // Check if deploy troops is available (check PLAYER resources, not target)
  const canDeployTroops = 
    playerCountry.resources.money >= 60 &&
    playerCountry.resources.military >= 20 &&  // FIXED: Was 30, should be 20 to match backend
    playerCountry.resources.influence >= 15;
  
  const deployTroopsAvailable = canDeployTroops && !isDeployTroopsLocked;
  
  // Check if air strike is available (percentage-based costs) OR on cooldown OR turn 1
  const airStrikeCooldown = playerCountry.air_strike_cooldown || 0;
  const isFirstTurn = game.current_turn === 1;
  const canAirStrike = 
    playerCountry.resources.money >= 10 && // Need at least $10M (15% of something)
    playerCountry.resources.military >= 5 && // Need at least 5 military
    playerCountry.resources.influence >= 5 && // Need at least 5 influence
    airStrikeCooldown === 0 && // Must not be on cooldown
    !isFirstTurn; // Cannot use on turn 1

  const actions = [
    { 
      type: 'propaganda', 
      label: 'PROPAGANDA', 
      icon: Megaphone, 
      cost: `${propagandaChance}% CHANCE`, 
      desc: getMissingResourcesTooltip('propaganda')
    },
    { 
      type: 'blackmail', 
      label: 'BLACKMAIL', 
      icon: KeyRound, 
      cost: `${blackmailChance}% CHANCE`, 
      desc: getMissingResourcesTooltip('blackmail')
    },
    { 
      type: 'air_strike', 
      label: airStrikeCooldown > 0 ? '✈️ RESUPPLYING' : isFirstTurn ? '🔒 LOCKED' : (canAirStrike ? 'AIR STRIKE ✈️' : 'AIR STRIKE 🔒'),
      icon: Plane, 
      cost: airStrikeCooldown > 0 ? `${airStrikeCooldown} ${airStrikeCooldown === 1 ? 'TURN' : 'TURNS'}` : isFirstTurn ? 'TURN 2+' : (canAirStrike ? '15% $ + 10% MIL + 10% INF' : 'NEED RESOURCES'),
      desc: airStrikeCooldown > 0 
        ? `🔧 Bombers are being resupplied. Available again in ${airStrikeCooldown} ${airStrikeCooldown === 1 ? 'turn' : 'turns'}. (Cooldown from previous bomber loss)` 
        : isFirstTurn 
        ? `🔒 Air strikes are LOCKED on turn 1. All nations need 1 turn to prepare their air forces. Air strikes unlock on turn 2!` 
        : getMissingResourcesTooltip('air_strike'),
      disabled: !canAirStrike || airStrikeCooldown > 0 || isFirstTurn
    },
    { 
      type: 'deploy_troops', 
      label: isDeployTroopsLocked ? 'DEPLOY TROOPS 🔒' : (deployTroopsAvailable ? 'DEPLOY TROOPS' : 'DEPLOY TROOPS 🔒'),
      icon: Shield, 
      cost: isDeployTroopsLocked ? '🔒 LOCKED' : (deployTroopsAvailable ? '$60M + 20 MIL + 15 INF' : 'NEED RESOURCES'),
      desc: getMissingResourcesTooltip('deploy_troops'),
      disabled: !deployTroopsAvailable || isDeployTroopsLocked
    },
    { 
      type: 'train_military', 
      label: 'TRAIN MILITARY', 
      icon: Swords, 
      cost: '$20M',
      desc: getMissingResourcesTooltip('train_military'),
      disabled: false
    },
    {
      type: 'install_radar',
      label: '🛰️ INSTALL RADAR',
      icon: Radar,
      cost: '$800M',
      desc: (() => {
        const hasRadar = playerCountry.has_radar || false;
        const current = playerCountry.resources;
        
        if (hasRadar) {
          return `✅ RADAR ALREADY INSTALLED\n\nYour radar dish is operational!\n\n📡 AUTO INTEL EACH TURN:\n• Automatic intelligence reports\n• Enemy resources revealed\n• No additional cost per turn\n\n⚠️ RISK:\n• 15% destruction chance per air strike\n• Must rebuy if destroyed ($800M)`;
        }
        
        if (current.money < 800) {
          return `⚠️ INSUFFICIENT FUNDS\n\nCost: $800M (one-time purchase)\nYou have: $${Math.round(current.money)}M\nNeed: $${800 - Math.round(current.money)}M more\n\n🛰️ RADAR INSTALLATION\n\nOne-time purchase for auto intel!\n\n📡 BENEFITS:\n• Automatic intel reports each turn\n• Reveals enemy resources/status\n• Permanent (until destroyed)\n\n⚠️ RISK:\n• 15% chance destroyed per air strike\n• Must rebuy if lost`;
        }
        
        return `🛰️ RADAR INSTALLATION\n\nOne-time purchase for auto intel!\n\nCost: 💵 $800M (one-time only)\n\n📡 BENEFITS:\n• Automatic intel reports each turn\n• Reveals enemy resources/status\n• Permanent (until destroyed)\n\n⚠️ RISK:\n• 15% chance destroyed per air strike\n• Must rebuy if lost ($800M)\n\nIntel is power!`;
      })(),
      disabled: (() => {
        const hasRadar = playerCountry.has_radar || false;
        if (hasRadar) return true; // Already installed
        return playerCountry.resources.money < 800;
      })()
    },
    {
      type: 'build_aa_defense',
      label: (() => {
        const currentAA = playerCountry.air_defense_level || 0;
        if (currentAA === 0) return 'PURCHASE AA 🛡️';
        if (currentAA === 1) return 'UPGRADE AA → T2 🛡️';
        if (currentAA === 2) return 'UPGRADE AA → T3 🛡️';
        return null; // Tier 3 maxed - don't show button
      })(),
      icon: Shield,
      cost: (() => {
        const currentAA = playerCountry.air_defense_level || 0;
        if (currentAA >= 3) return null;
        const targetTier = currentAA + 1;
        const baseCosts = { 1: 500, 2: 1000, 3: 1500 };
        const population = playerCountry.resources?.population || 100;
        const multiplier = Math.max(0.8, Math.min(3.0, 0.8 + population / 500));
        const actualCost = Math.floor(baseCosts[targetTier] * multiplier);
        return `$${actualCost}M`;
      })(),
      desc: (() => {
        const currentAA = playerCountry.air_defense_level || 0;
        if (currentAA >= 3) return '🛡️ MAXED OUT!\n\nYou have elite AA defenses (Tier 3).\nEnemy air strikes have only 35% success rate against you!';
        
        const targetTier = currentAA + 1;
        const baseCosts = { 1: 500, 2: 1000, 3: 1500 };
        const population = playerCountry.resources?.population || 100;
        const multiplier = Math.max(0.8, Math.min(3.0, 0.8 + population / 500));
        const actualCost = Math.floor(baseCosts[targetTier] * multiplier);
        const current = playerCountry.resources;
        
        const defenseBonuses = { 1: 15, 2: 25, 3: 35 };
        const defenseBonus = defenseBonuses[targetTier];
        const enemySuccessRate = 70 - defenseBonus;
        
        const tierNames = {
          1: 'Basic AA Defenses',
          2: 'Advanced AA Systems',
          3: 'Elite AA Network'
        };
        
        if (current.money < actualCost) {
          return `⚠️ INSUFFICIENT FUNDS\n\nCost: $${actualCost}M\nYou have: $${Math.round(current.money)}M\nNeed: $${actualCost - Math.round(current.money)}M more\n\n${tierNames[targetTier]} (Tier ${targetTier})\nDefense: +${defenseBonus}%\nEnemy strike success: ${enemySuccessRate}% (down from 70%)`;
        }
        
        return `🛡️ ${tierNames[targetTier].toUpperCase()} (TIER ${targetTier})\n\nCost: $${actualCost}M (base: $${baseCosts[targetTier]}M × ${multiplier.toFixed(2)}x)\n\nDefense Bonus: +${defenseBonus}%\nEnemy Air Strike Success: ${enemySuccessRate}% (down from 70%)\n\n${currentAA > 0 ? `Current: Tier ${currentAA}` : 'No AA defenses yet'}\n\n💡 Protects against enemy bombers!`;
      })(),
      disabled: (playerCountry.air_defense_level || 0) >= 3 || playerCountry.resources.money < (() => {
        const currentAA = playerCountry.air_defense_level || 0;
        if (currentAA >= 3) return 0;
        const targetTier = currentAA + 1;
        const baseCosts = { 1: 500, 2: 1000, 3: 1500 };
        const population = playerCountry.resources?.population || 100;
        const multiplier = Math.max(0.8, Math.min(3.0, 0.8 + population / 500));
        return Math.floor(baseCosts[targetTier] * multiplier);
      })()
    },
    { 
      type: 'diplomacy', 
      label: 'DIPLOMACY', 
      icon: Handshake, 
      cost: `${diplomacyChance}% CHANCE`, 
      desc: getMissingResourcesTooltip('diplomacy')
    },
    { 
      type: 'nuclear_strike', 
      label: 'NUCLEAR STRIKE', 
      icon: Radiation, 
      cost: '$50M + 30 INF',
      desc: getMissingResourcesTooltip('nuclear_strike'),
      disabled: false  // Player can always select nuke action if they have enrichment (checked in GameScreen)
    },
    { 
      type: 'skip_turn', 
      label: '⏭️ SKIP ROUND', 
      icon: ChevronRight, 
      cost: 'FREE',
      desc: `⏭️ SKIP YOUR TURN\n\nPass this turn without taking any risky actions.\n\nUse this when:\n• All success chances are too low\n• Waiting for air strikes to resupply\n• Saving resources for next turn\n• Waiting for better opportunities\n\n✅ No penalty - just ends your turn immediately!`,
      disabled: false
    },
  ];

  return (
    <div 
      className="bunker-panel p-4 w-80 bg-slate-900/75 backdrop-blur-md relative z-50 cursor-pointer hover:bg-slate-900/85 transition-colors pointer-events-auto" 
      data-testid="country-panel"
      onClick={(e) => {
        console.log('🖱️ CountryPanel clicked! Opening Status Report...');
        e.stopPropagation();
        onStatusReportClick && onStatusReportClick();
      }}
      title="Click to view full Status Report"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="font-heading text-xl tracking-wider" style={{ color: country.color }}>
            {country.name}
          </div>
          <div className="text-xs text-muted uppercase tracking-widest">
            {country.leader_title}: {country.leader_name}
          </div>
          <div className="text-xs text-blue-400 mt-1 opacity-75">
            📊 Click for full report
          </div>
        </div>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }} 
          className="text-muted hover:text-white" 
          data-testid="close-panel-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Resources */}
      <div className="grid grid-cols-3 gap-2 mb-3 text-sm">
        <div className="flex items-center gap-2">
          <UserCheck className="w-4 h-4 text-purple-500" />
          <span className="font-mono">{country.resources.population}M</span>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-green-500" />
          <span className="font-mono">${country.resources.money}M</span>
        </div>
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-500" />
          <span className="font-mono">{country.resources.influence}</span>
        </div>
        <div className="flex items-center gap-2">
          <Swords className="w-4 h-4 text-red-500" />
          <span className="font-mono">{country.resources.military}</span>
        </div>
      </div>
      
      {/* Uranium Enrichment Progress - REDUCED SPACING */}
      <div className="mb-2 p-2 bg-yellow-900/20 border border-yellow-500/30 rounded">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs text-yellow-400 uppercase tracking-wide flex items-center gap-1">
            <Radiation className="w-3 h-3" />
            {(country.resources?.nukes_ready > 0 || (country.resources?.uranium_enrichment >= 100)) ? '☢️ NUKE READY' : 'ENRICHMENT'}
          </div>
          <div className="text-xs font-mono text-yellow-300 font-bold">
            {Math.floor(country.resources.uranium_enrichment || 0)}%
          </div>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all ${
              (country.resources?.nukes_ready > 0 || (country.resources?.uranium_enrichment >= 100)) ? 'bg-red-500 animate-pulse' : 'bg-yellow-500'
            }`}
            style={{ width: `${Math.min(country.resources.uranium_enrichment || 0, 100)}%` }}
          />
        </div>
        {(country.resources?.nukes_ready > 0 || (country.resources?.uranium_enrichment >= 100)) && (
          <div className="text-xs text-red-400 mt-1 text-center font-bold animate-pulse">
            ⚠️ NUCLEAR WEAPON ARMED ⚠️
          </div>
        )}
      </div>

      {/* ☢️ RADIATION EFFECTS - Now shows on hover over radiation glow on map */}
      {/* Removed large panel to save space - hover over orange radiation circle to see details */}

      {/* Relations (if not own country) - REDUCED SPACING */}
      {!isOwnCountry && (
        <div className="mb-2 p-2 bg-black/30 rounded">
          <div className="text-xs text-muted uppercase tracking-widest mb-1">RELATIONS</div>
          <div className="flex items-center justify-between">
            <span className="font-mono" style={{ color: relationInfo.color }}>
              {relationInfo.text}
            </span>
            <span className="font-mono text-sm">{relation}</span>
          </div>
          <div className="w-full h-1 bg-neutral-800 rounded mt-1">
            <div
              className="h-full rounded transition-all"
              style={{
                width: `${((relation + 100) / 200) * 100}%`,
                backgroundColor: relationInfo.color
              }}
            />
          </div>
        </div>
      )}

      {/* Actions (if not own country) - 2 COLUMN GRID with READABLE sizes */}
      {!isOwnCountry && (
        <div>
          <div className="text-xs text-muted uppercase tracking-widest mb-2">ACTIONS</div>
          {!canTakeAction && (
            <div className="p-3 bg-red-900/20 border border-red-500/30 rounded text-center mb-2">
              <div className="text-red-400 font-mono uppercase text-sm">Not Your Turn!</div>
            </div>
          )}
          {/* 2-column grid for actions - WITH GLOW for important actions */}
          <div className="grid grid-cols-2 gap-2">
            {actions.filter(action => action.label !== null).map(({ type, label, icon: Icon, cost, desc, disabled }) => {
              // Check if this is an important available action
              const isAirStrikeReady = type === 'air_strike' && !disabled && canTakeAction;
              const isNukeReady = type === 'nuclear_strike' && !disabled && canTakeAction;
              const shouldGlow = isAirStrikeReady || isNukeReady;
              const isOnboardingHint = type === hintActionType && !disabled && canTakeAction;
              
              // Special styling for SKIP ROUND (white/light theme)
              const isSkipRound = type === 'skip_turn';
              
              return (
                <button
                  key={type}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction(type, country.code);
                    
                    // ⏭️ Close country panel immediately when skipping turn
                    if (isSkipRound) {
                      onClose();
                    }
                  }}
                  disabled={!canTakeAction || disabled}
                  className={`relative p-2 text-left flex items-start gap-2 rounded transition-all overflow-visible
                    ${!canTakeAction || disabled ? 'opacity-50 cursor-not-allowed' : ''}
                    ${isOnboardingHint ? 'ring-2 ring-green-400 ring-offset-1 ring-offset-slate-900' : ''}
                    ${type === 'nuclear_strike'
                      ? 'bg-red-900/30 hover:bg-red-900/50 border border-red-500/30' 
                      : isSkipRound
                      ? 'bg-slate-700/40 hover:bg-slate-600/50 border-2 border-slate-400/40'
                      : type === 'deploy_troops'
                      ? 'bg-red-900/30 hover:bg-red-900/50 border border-red-500/30'
                      : 'bg-neutral-800/50 hover:bg-neutral-700/50 border border-neutral-700/30'}
                  `}
                  style={isOnboardingHint ? {
                    boxShadow: '0 0 14px rgba(74, 222, 128, 0.7), 0 0 28px rgba(74, 222, 128, 0.3)',
                    animation: 'pulse 1.6s ease-in-out infinite'
                  } : shouldGlow ? {
                    boxShadow: isNukeReady 
                      ? '0 0 15px rgba(239, 68, 68, 0.7), 0 0 30px rgba(239, 68, 68, 0.3)'
                      : '0 0 15px rgba(251, 191, 36, 0.7), 0 0 30px rgba(251, 191, 36, 0.3)',
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                  } : {}}
                  data-testid={`action-${type}`}
                  title={!canTakeAction ? `⏸️ NOT YOUR TURN\n\nWait for AI players to complete their actions.\nYou'll get ${playerCountry.actions_remaining || 3} actions when it's your turn again!` : desc}
                >
                  {/* 🎓 Onboarding hint label */}
                  {isOnboardingHint && (
                    <div
                      className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-green-500 text-black text-[9px] font-bold whitespace-nowrap pointer-events-none animate-bounce-soft-y"
                      style={{
                        boxShadow: '0 0 8px rgba(74, 222, 128, 0.8)',
                        zIndex: 20,
                      }}
                      data-testid={`onboarding-hint-${type}`}
                    >
                      {hintLabel}
                    </div>
                  )}
                  {/* Rotating glow for ready actions */}
                  {shouldGlow && (
                    <div 
                      className="absolute inset-0 opacity-30 pointer-events-none"
                      style={{
                        background: isNukeReady
                          ? 'conic-gradient(from 0deg, transparent, rgba(239, 68, 68, 0.6), transparent)'
                          : 'conic-gradient(from 0deg, transparent, rgba(251, 191, 36, 0.6), transparent)',
                        animation: 'spin 3s linear infinite',
                        filter: 'blur(8px)'
                      }}
                    ></div>
                  )}
                  
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 z-10 ${
                    type === 'nuclear_strike' || type === 'deploy_troops' 
                      ? 'text-red-500' 
                      : isSkipRound
                      ? 'text-slate-300'
                      : 'text-amber-500'
                  } ${shouldGlow ? 'animate-pulse' : ''}`} />
                  <div className="flex-1 min-w-0 z-10">
                    <div className={`text-xs font-mono uppercase leading-tight ${
                      isSkipRound ? 'text-slate-200' : ''
                    } ${shouldGlow ? 'animate-pulse' : ''}`}>
                      {label}
                    </div>
                    <div className={`text-xs leading-tight mt-0.5 ${
                      isSkipRound ? 'text-slate-400' : 'text-amber-400'
                    }`}>{cost}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isOwnCountry && (
        <div>
          <div className="p-3 bg-green-900/20 border border-green-500/30 rounded text-center mb-3">
            <div className="text-green-400 font-mono uppercase">YOUR NATION</div>
          </div>
          
          {/* AA DEFENSE STATUS - Show launcher graphic and tier info */}
          <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-blue-400 uppercase tracking-wide flex items-center gap-1">
                <Shield className="w-3 h-3" />
                AA DEFENSE SYSTEM
              </div>
              <div className="text-xs font-mono text-blue-300 font-bold">
                TIER {country.air_defense_level || 0}
              </div>
            </div>
            
            {/* AA Launcher Graphic */}
            {country.air_defense_level > 0 && (
              <div className="flex items-center justify-center mb-2">
                <img 
                  src={`/assets/aa-tier-${country.air_defense_level}.png`}
                  alt={`AA Tier ${country.air_defense_level}`}
                  className="w-20 h-20 object-contain drop-shadow-lg"
                  style={{
                    filter: 'drop-shadow(0 0 15px rgba(59, 130, 246, 0.6))'
                  }}
                />
              </div>
            )}
            
            {/* Progress bar showing tier level */}
            <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
              <div 
                className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all"
                style={{ width: `${((country.air_defense_level || 0) / 3) * 100}%` }}
              />
            </div>
            
            {/* Status text */}
            <div className="text-xs text-center">
              {country.air_defense_level === 0 && (
                <span className="text-gray-400">🔒 No AA Defense Installed</span>
              )}
              {country.air_defense_level === 1 && (
                <span className="text-blue-400">🛡️ Basic Protection Active</span>
              )}
              {country.air_defense_level === 2 && (
                <span className="text-cyan-400">🛡️🛡️ Enhanced Protection Active</span>
              )}
              {country.air_defense_level === 3 && (
                <span className="text-green-400 font-bold">🛡️🛡️🛡️ MAXIMUM PROTECTION</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Action log - COMPACT VERSION (Half height with sticky header)
export function ActionLog({ logs }) {
  const scrollRef = useRef(null);
  const [prevLogsLength, setPrevLogsLength] = useState(0);

  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    if (logs && logs.length > prevLogsLength) {
      if (scrollRef.current) {
        // Scroll to bottom to show newest entries
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
      setPrevLogsLength(logs.length);
    }
  }, [logs, prevLogsLength]);

  if (!logs || logs.length === 0) return null;

  return (
    <div 
      className="bunker-panel bg-slate-900/75 backdrop-blur-md max-h-48 overflow-hidden flex flex-col" 
      data-testid="action-log"
    >
      {/* Sticky header - always visible */}
      <div className="text-xs text-muted uppercase tracking-widest p-3 md:p-4 pb-2 flex items-center justify-between bg-slate-900/90 sticky top-0 z-10">
        <span>INTELLIGENCE FEED</span>
        <span className="text-amber-500 font-bold">{logs.length} RECORDS</span>
      </div>
      
      {/* Scrollable content.
          🐛 FIX: removed `flex-1`. With it set, the panel always grew
          to fill `max-h-48` (192px) even when only 1-2 entries existed —
          leaving a fat empty band on mobile between the feed and the
          ResourceBar below. Without flex-1 the panel sizes to its
          content and only scrolls when entries exceed max-h-48. */}
      <div 
        ref={scrollRef}
        className="overflow-y-auto px-3 md:px-4 pb-3 md:pb-4 pt-2 scroll-smooth"
      >
        <div className="space-y-1.5 md:space-y-2">
          {logs.map((log, i) => (
            <div 
              key={`${log.turn}-${i}`} 
              className="text-xs font-mono text-neutral-400 border-l-2 border-amber-500/30 pl-2 py-1 animate-fadeIn"
            >
              <span className="text-amber-500 font-bold">[T{log.turn}]</span> {log.message}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Notification toast
export function GameNotification({ message, type = 'info', onClose }) {
  // 📱 Mobile-friendly toast: much more transparent so the action playing
  // out behind it stays visible; smaller max-width on phones so the box
  // doesn't dominate the screen.
  const colors = {
    info: 'border-blue-500 bg-blue-900/40 text-blue-100',
    success: 'border-yellow-500 bg-yellow-700/40 text-yellow-50',
    warning: 'border-amber-500 bg-amber-900/40 text-amber-100',
    danger: 'border-red-500 bg-red-800/40 text-white',
  };
  
  const icons = {
    info: 'ℹ️',
    success: '✨',  // Sparkles for golden success
    warning: '⚡',
    danger: '❌',   // X for red failure
  };

  return (
    <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 px-3 py-2 md:p-4 rounded border-l-4 ${colors[type]} backdrop-blur-md max-w-[90vw] md:max-w-2xl animate-slide-in shadow-2xl`} data-testid="game-notification">
      <button onClick={onClose} className="float-right ml-3 opacity-80 hover:opacity-100" data-testid="game-notification-close-btn">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-2">
        <span className="text-xl md:text-2xl">{icons[type]}</span>
        <div className="font-mono text-xs md:text-sm font-semibold leading-snug">{message}</div>
      </div>
    </div>
  );
}

// Action Result Modal - Shows detailed outcome of actions
// Animated Number Component - counts from 0 to target value
function AnimatedNumber({ value, suffix = '', isPercentage = false, duration = 1000, playSound = false }) {
  const [displayValue, setDisplayValue] = React.useState(0);
  const soundPlayedRef = React.useRef(false);
  
  React.useEffect(() => {
    const isPositive = value > 0;
    const absoluteValue = Math.abs(value);
    
    // Play custom meter sound once
    if (playSound && !soundPlayedRef.current && value !== 0) {
      const soundFile = isPositive 
        ? '/assets/audio/meter-going-up.mp3'    // Your custom "going up" sound
        : '/assets/audio/meter-going-down.mp3'; // Your custom "going down" sound
      const audio = new Audio(soundFile);
      audio.volume = 0.4;
      audio.play().catch(e => console.log('Meter sound failed:', e));
      soundPlayedRef.current = true;
    }
    
    // Animate number counting
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease-out animation
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      const current = absoluteValue * easeProgress;
      
      setDisplayValue(isPositive ? current : -current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
      }
    };
    
    animate();
  }, [value, duration, playSound]);
  
  const formatted = isPercentage 
    ? `${displayValue > 0 ? '+' : ''}${Math.round(displayValue)}%`
    : `${displayValue > 0 ? '+' : ''}${Math.round(displayValue)}${suffix}`;
  
  return <span>{formatted}</span>;
}

export function ActionResultModal({ result, sourceCountry, targetCountry, onClose }) {
  const [animatedSourceEnrichment, setAnimatedSourceEnrichment] = React.useState(0);
  const [animatedTargetEnrichment, setAnimatedTargetEnrichment] = React.useState(0);
  
  // Animate enrichment percentages on mount
  React.useEffect(() => {
    if (!result || !sourceCountry || !targetCountry) return; // Skip animation if no data
    
    const sourceEnrichment = sourceCountry?.resources?.uranium_enrichment || 0;
    const targetEnrichment = targetCountry?.resources?.uranium_enrichment || 0;
    
    // Reset to 0 first, then animate
    setAnimatedSourceEnrichment(0);
    setAnimatedTargetEnrichment(0);
    
    // Animate from 0 to current value
    const duration = 1500; // 1.5 seconds
    const steps = 60;
    const stepDuration = duration / steps;
    
    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      
      setAnimatedSourceEnrichment(Math.round(sourceEnrichment * progress));
      setAnimatedTargetEnrichment(Math.round(targetEnrichment * progress));
      
      if (currentStep >= steps) {
        clearInterval(interval);
        setAnimatedSourceEnrichment(sourceEnrichment);
        setAnimatedTargetEnrichment(targetEnrichment);
      }
    }, stepDuration);
    
    return () => clearInterval(interval);
  }, [result, sourceCountry, targetCountry]);

  // Play action-specific audio
  React.useEffect(() => {
    if (!result) return;
    
    if (result.type === 'nuclear_strike') {
      const alarm = new Audio('/assets/audio/nuke-launch-alarm.mp3');
      alarm.volume = 0.8;
      alarm.loop = true; // Loop the alarm while modal is open
      
      alarm.play().catch(e => console.log('Nuke alarm play failed:', e));
      
      return () => {
        alarm.pause();
        alarm.currentTime = 0;
      };
    }
    
    // Deploy troops layered audio (music + sound effect)
    if (result.type === 'deploy_troops') {
      // Randomly select sound effect (1 of 3)
      const soundEffects = [
        '/assets/audio/deploy-sound-1.mp3',
        '/assets/audio/deploy-sound-2.mp3',
        '/assets/audio/deploy-sound-3.mp3'
      ];
      const selectedSound = soundEffects[Math.floor(Math.random() * soundEffects.length)];
      
      // Randomly select music (1 of 2)
      const musicTracks = [
        '/assets/audio/deploy-music-1.mp3',
        '/assets/audio/deploy-music-2.mp3'
      ];
      const selectedMusic = musicTracks[Math.floor(Math.random() * musicTracks.length)];
      
      const soundEffect = new Audio(selectedSound);
      soundEffect.volume = 0.7;
      
      const music = new Audio(selectedMusic);
      music.volume = 0.5;
      
      soundEffect.play().catch(e => console.log('Deploy sound failed:', e));
      music.play().catch(e => console.log('Deploy music failed:', e));
      
      return () => {
        soundEffect.pause();
        soundEffect.currentTime = 0;
        music.pause();
        music.currentTime = 0;
      };
    }
    
    // Note: Blackmail audio is now handled by BlackmailAnimation component
  }, [result]);
  
  // Early return AFTER hooks
  // Helper function to get random emotion sprite for a country
  const getRandomEmotionSprite = (countryCode, emotion) => {
    const basePath = '/assets/leaders';
    const lowerCode = countryCode.toLowerCase();
    const spriteMap = {
      'USA': 'usa',
      'RUS': 'russia',
      'CHN': 'china',
      'EU': 'europe',
      'NK': 'nkorea'
    };
    const spriteName = spriteMap[countryCode] || lowerCode;
    
    // Map of available emotion sprites for each country
    const emotionSprites = {
      'USA': {
        'happy': ['usa-happy.png', 'usa-happy-2.png', 'usa-happy-3.png', 'usa-happy-4.png', 'usa-happy-5.png'],
        'scared': ['usa-scared.png', 'usa-scared-2.png', 'usa-scared-3.png', 'usa-scared-4.png'],
        'angry': ['usa-angry.png', 'usa-angry-2.png']
      },
      'RUS': {
        'happy': ['russia-happy.png', 'russia-happy-2.png', 'russia-happy-3.png'],
        'scared': ['russia-scared.png'],
        'angry': ['russia-angry.png', 'russia-angry-2.png', 'russia-angry-3.png']
      },
      'CHN': {
        'happy': ['china-happy.png', 'china-happy-2.png'],
        'scared': ['china-scared.png', 'china-scared-2.png', 'china-scared-3.png', 'china-scared-4.png'],
        'angry': ['china-angry.png', 'china-angry-2.png']
      },
      'EU': {
        'happy': ['europe-happy.png', 'europe-happy-2.png', 'europe-happy-3.png'],
        'scared': ['europe-scared.png'],
        'angry': ['europe-angry.png', 'europe-angry-2.png']
      },
      'NK': {
        'happy': ['nkorea-happy.png', 'nkorea-happy-2.png', 'nkorea-happy-3.png', 'nkorea-happy-4.png'],
        'scared': ['nkorea-scared.png', 'nkorea-scared-2.png', 'nkorea-scared-3.png', 'nkorea-scared-4.png'],
        'angry': ['nkorea-angry.png', 'nkorea-angry-2.png', 'nkorea-angry-3.png', 'nkorea-angry-4.png']
      }
    };
    
    const countrySprites = emotionSprites[countryCode];
    if (!countrySprites || !countrySprites[emotion]) {
      // Fallback to basic sprite
      return `${basePath}/${spriteName}-${emotion}.png`;
    }
    
    // Randomly select from available sprites
    const availableSprites = countrySprites[emotion];
    const randomIndex = Math.floor(Math.random() * availableSprites.length);
    return `${basePath}/${availableSprites[randomIndex]}`;
  };

  // Get emotion sprites for source and target (randomly selected once per modal open)
  const [sourceEmotionSprite] = React.useState(() => {
    // Safety check inside useState callback
    if (!result || !sourceCountry || !sourceCountry.code) {
      return '/assets/leaders/usa.png'; // Fallback
    }
    
    // Source emotion logic
    if (result.success) {
      return getRandomEmotionSprite(sourceCountry.code, 'happy');
    } else {
      // Failed action - source is scared/frustrated
      return getRandomEmotionSprite(sourceCountry.code, 'scared');
    }
  });
  
  const [targetEmotionSprite] = React.useState(() => {
    // Safety check inside useState callback
    if (!result || !targetCountry || !targetCountry.code) {
      return '/assets/leaders/russia.png'; // Fallback
    }
    
    // Target emotion logic - show ANGRY for aggressive actions!
    const aggressiveActions = ['air_strike', 'nuclear_strike', 'deploy_troops', 'blackmail', 'propaganda'];
    const isAggressive = aggressiveActions.includes(result.action);
    
    if (result.success) {
      // Target got hit successfully - show ANGRY for aggressive actions, SCARED for overwhelming ones
      if (result.action === 'nuclear_strike') {
        return getRandomEmotionSprite(targetCountry.code, 'scared'); // Nukes are terrifying
      } else if (isAggressive) {
        return getRandomEmotionSprite(targetCountry.code, 'angry'); // ANGRY when attacked!
      } else {
        return getRandomEmotionSprite(targetCountry.code, 'scared'); // Default scared
      }
    } else {
      // Target defended successfully - show HAPPY (victory!)
      return getRandomEmotionSprite(targetCountry.code, 'happy');
    }
  });

  // Safety check AFTER all hooks - early return is OK here
  if (!result || !sourceCountry || !targetCountry) {
    console.error('ActionResultModal: Missing required props', { result, sourceCountry, targetCountry });
    return null;
  }

  const getActionIcon = (actionType) => {
    const icons = {
      propaganda: Megaphone,
      blackmail: KeyRound,
      deploy_troops: Shield,
      diplomacy: Handshake,
      nuclear_strike: Radiation
    };
    return icons[actionType] || AlertTriangle;
  };

  // Get leader sprite path with smart fallbacks
  const getLeaderSprite = (countryCode, emotion) => {
    const spriteMap = {
      'USA': 'usa',
      'RUS': 'russia',
      'CHN': 'china',
      'EU': 'europe',
      'NK': 'nkorea'
    };
    const spriteName = spriteMap[countryCode] || countryCode.toLowerCase();
    
    if (!emotion || emotion === 'normal') {
      return `/assets/leaders/${spriteName}.png`;
    }
    
    // Fallback: if requesting angry but might not exist, use scared
    if (emotion === 'angry') {
      return `/assets/leaders/${spriteName}-scared.png`;
    }
    
    return `/assets/leaders/${spriteName}-${emotion}.png`;
  };

  // Determine emotions based on success/failure
  const sourceEmotion = result.success ? 'happy' : 'scared';
  const targetEmotion = result.success ? 'scared' : 'happy';

  const ActionIcon = getActionIcon(result.type);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-auto">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={onClose}
      />
      
      {/* Modal - Semi-transparent. max-h + overflow allow scrolling on
          short viewports (landscape phones). On mobile we shrink the
          inner content via App.css scaling.
          📱 Tighter padding, smaller max-w on mobile so the modal doesn't
          fill the entire screen. */}
      <div 
        data-testid="action-result-modal"
        className="relative p-3 md:p-8 max-w-sm md:max-w-4xl w-full mx-2 md:mx-4 border-2 md:border-4 border-amber-500/50 rounded-lg bg-slate-900/85 backdrop-blur-xl shadow-2xl max-h-[88vh] overflow-y-auto"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 md:top-4 md:right-4 text-neutral-400 hover:text-white transition-colors z-10"
          data-testid="close-action-modal"
        >
          <X className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        {/* Header with Leaders */}
        <div className="text-center mb-3 md:mb-6 relative">
          {/* Leaders flanking the action header */}
          <div className="flex items-center justify-center gap-2 md:gap-8 mb-3 md:mb-6">
            {/* Source Leader (Left) */}
            <div className="flex flex-col items-center gap-1 md:gap-2">
              <img 
                src={sourceEmotionSprite}
                alt={sourceCountry?.name}
                className={`w-16 h-16 md:w-40 md:h-40 object-contain ${
                  sourceEmotion === 'scared' ? 'leader-scared' : 
                  sourceEmotion === 'happy' ? 'leader-happy' : 
                  sourceEmotion === 'angry' ? 'leader-angry' : ''
                }`}
                style={{
                  filter: 'drop-shadow(0 0 25px rgba(59, 130, 246, 0.8))'
                }}
                onError={(e) => {
                  console.log(`Failed to load: ${e.target.src}`);
                  // Fallback to normal sprite
                  e.target.src = getLeaderSprite(sourceCountry?.code, 'normal');
                }}
              />
              <div className="text-blue-400 font-bold text-[10px] md:text-sm leading-tight">{sourceCountry?.name}</div>
              
              {/* Enrichment Meter — hidden on mobile to save vertical space */}
              <div className="w-32 mt-1 hidden md:block">
                <div className="flex items-center gap-1 mb-1">
                  <Radiation className="w-3 h-3 text-amber-500" />
                  <span className="text-xs text-amber-400 uppercase tracking-wide">Enrichment</span>
                  <span className="text-xs text-amber-400 font-mono font-bold ml-auto">{animatedSourceEnrichment}%</span>
                </div>
                <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden border border-amber-500/30">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 to-red-500 transition-all duration-1000 ease-out"
                    style={{ width: `${animatedSourceEnrichment}%` }}
                  />
                </div>
              </div>
            </div>
            
            {/* Action Result Box */}
            <div className={`inline-flex flex-col items-center gap-1 md:gap-3 px-3 py-2 md:px-10 md:py-5 rounded-lg ${
              result.success 
                ? 'bg-green-900/40 border-2 border-green-500/60' 
                : 'bg-red-900/40 border-2 border-red-500/60'
            }`}>
              <ActionIcon className={`w-6 h-6 md:w-12 md:h-12 ${result.success ? 'text-green-500' : 'text-red-500'}`} />
              <div className="text-xs md:text-3xl font-heading tracking-wider uppercase leading-tight">
                {result.type?.replace('_', ' ')}
              </div>
              <div className={`text-sm md:text-2xl font-bold ${result.success ? 'text-green-400' : 'text-red-400'}`}>
                {result.success ? '✓ SUCCESS' : '✗ FAILED'}
              </div>
            </div>

            {/* Target Leader (Right) */}
            <div className="flex flex-col items-center gap-1 md:gap-2">
              <img 
                src={targetEmotionSprite}
                alt={targetCountry?.name}
                className={`w-16 h-16 md:w-40 md:h-40 object-contain ${
                  targetEmotion === 'scared' ? 'leader-scared' : 
                  targetEmotion === 'happy' ? 'leader-happy' : 
                  targetEmotion === 'angry' ? 'leader-angry' : ''
                }`}
                style={{
                  filter: 'drop-shadow(0 0 25px rgba(239, 68, 68, 0.8))'
                }}
                onError={(e) => {
                  console.log(`Failed to load: ${e.target.src}`);
                  // Fallback to normal sprite
                  e.target.src = getLeaderSprite(targetCountry?.code, 'normal');
                }}
              />
              <div className="text-red-400 font-bold text-[10px] md:text-sm leading-tight">{targetCountry?.name}</div>
              
              {/* Enrichment Meter — hidden on mobile to save vertical space */}
              <div className="w-32 mt-1 hidden md:block">
                <div className="flex items-center gap-1 mb-1">
                  <Radiation className="w-3 h-3 text-amber-500" />
                  <span className="text-xs text-amber-400 uppercase tracking-wide">Enrichment</span>
                  <span className="text-xs text-amber-400 font-mono font-bold ml-auto">{animatedTargetEnrichment}%</span>
                </div>
                <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden border border-amber-500/30">
                  <div 
                    className="h-full bg-gradient-to-r from-amber-500 to-red-500 transition-all duration-1000 ease-out"
                    style={{ width: `${animatedTargetEnrichment}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Countries involved - Arrow. Hidden on mobile to reduce vertical space
              (the names already show under each leader above). */}
          <div className="hidden md:flex items-center justify-center gap-4 text-lg mb-4">
            <span className="text-blue-400 font-bold">{sourceCountry?.name}</span>
            <ChevronRight className="w-6 h-6 text-amber-500" />
            <span className="text-red-400 font-bold">{targetCountry?.name}</span>
          </div>
        </div>

        {/* Results breakdown */}
        <div className="space-y-2 md:space-y-3 mb-3 md:mb-6">
          {result.effects && (
            <>
              {/* Population changes */}
              {(result.effects.population_gained || result.effects.population_lost) && (
                <div className="flex items-center justify-between p-2 md:p-3 bg-purple-900/20 rounded border border-purple-500/30">
                  <div className="flex items-center gap-2">
                    <UserCheck className="w-4 h-4 md:w-5 md:h-5 text-purple-400" />
                    <span className="text-xs md:text-sm uppercase tracking-wide">Population</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {result.effects.population_gained && (
                      <span className="text-green-400 font-mono text-sm md:text-lg">
                        <TrendingUp className="inline w-3 h-3 md:w-4 md:h-4" /> <AnimatedNumber value={result.effects.population_gained} suffix="M" playSound={true} />
                      </span>
                    )}
                    {result.effects.population_lost && (
                      <span className="text-red-400 font-mono text-sm md:text-lg">
                        <TrendingDown className="inline w-3 h-3 md:w-4 md:h-4" /> <AnimatedNumber value={result.effects.population_lost} suffix="M" playSound={true} />
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Money changes */}
              {(result.effects.money_gained || result.effects.money_lost || result.effects.funds_lost) && (
                <div className="flex items-center justify-between p-2 md:p-3 bg-green-900/20 rounded border border-green-500/30">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-green-400" />
                    <span className="text-xs md:text-sm uppercase tracking-wide">Funds</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {result.effects.money_gained && (
                      <span className="text-green-400 font-mono text-sm md:text-lg">
                        <TrendingUp className="inline w-3 h-3 md:w-4 md:h-4" /> $<AnimatedNumber value={result.effects.money_gained} suffix="M" playSound={true} />
                      </span>
                    )}
                    {(result.effects.money_lost || result.effects.funds_lost) && (
                      <span className="text-red-400 font-mono text-sm md:text-lg">
                        <TrendingDown className="inline w-3 h-3 md:w-4 md:h-4" /> -$<AnimatedNumber value={Math.abs(result.effects.money_lost || result.effects.funds_lost)} suffix="M" playSound={true} />
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Influence changes */}
              {(result.effects.influence_gained || result.effects.influence_lost) && (
                <div className="flex items-center justify-between p-2 md:p-3 bg-blue-900/20 rounded border border-blue-500/30">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 md:w-5 md:h-5 text-blue-400" />
                    <span className="text-xs md:text-sm uppercase tracking-wide">Influence</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {result.effects.influence_gained && (
                      <span className="text-green-400 font-mono text-sm md:text-lg">
                        <TrendingUp className="inline w-3 h-3 md:w-4 md:h-4" /> <AnimatedNumber value={result.effects.influence_gained} playSound={true} />
                      </span>
                    )}
                    {result.effects.influence_lost && (
                      <span className="text-red-400 font-mono text-sm md:text-lg">
                        <TrendingDown className="inline w-3 h-3 md:w-4 md:h-4" /> <AnimatedNumber value={result.effects.influence_lost} playSound={true} />
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Propaganda boost */}
              {result.effects.propaganda_increase !== undefined && (
                <div className="flex items-center justify-between p-2 md:p-3 bg-amber-900/20 rounded border border-amber-500/30">
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 md:w-5 md:h-5 text-amber-400" />
                    <span className="text-xs md:text-sm uppercase tracking-wide">Next Propaganda</span>
                  </div>
                  <span className={`font-mono text-sm md:text-lg ${result.effects.propaganda_increase >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {result.effects.propaganda_increase >= 0 ? (
                      <><TrendingUp className="inline w-3 h-3 md:w-4 md:h-4" /> <AnimatedNumber value={result.effects.propaganda_increase} isPercentage={true} playSound={true} /></>
                    ) : (
                      <><TrendingDown className="inline w-3 h-3 md:w-4 md:h-4" /> <AnimatedNumber value={result.effects.propaganda_increase} isPercentage={true} playSound={true} /></>
                    )}
                  </span>
                </div>
              )}

              {/* Military changes */}
              {result.effects.military && (
                <div className="flex items-center justify-between p-2 md:p-3 bg-red-900/20 rounded border border-red-500/30">
                  <div className="flex items-center gap-2">
                    <Swords className="w-4 h-4 md:w-5 md:h-5 text-red-400" />
                    <span className="text-xs md:text-sm uppercase tracking-wide">Military</span>
                  </div>
                  <span className="text-green-400 font-mono text-sm md:text-lg">
                    <TrendingUp className="inline w-3 h-3 md:w-4 md:h-4" /> +{result.effects.military}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Message */}
        <div className="text-center p-2 md:p-4 bg-neutral-900/50 rounded border border-neutral-700 mb-3 md:mb-6">
          <p className="text-xs md:text-sm leading-relaxed text-neutral-300">
            {result.message}
          </p>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="w-full tactical-button py-2 md:py-3 text-sm md:text-lg"
          data-testid="continue-btn"
        >
          CONTINUE
        </button>
      </div>
    </div>
  );
}
